import { z } from 'zod';

var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/rate-limit-tiers.defaults.json
var rate_limit_tiers_defaults_default = {
  default: { ttl: 6e4, limit: 100 },
  auth: { ttl: 6e4, limit: 5 },
  expensive: { ttl: 36e5, limit: 10 },
  mcp: { ttl: 6e4, limit: 60 }
};

// src/admins.ts
var MAX_ADMINS_PER_TENANT = 5;
var TENANT_SIGN_IN_PROVIDERS = [
  "google",
  "entra-saml"
];
var TenantSignInProviderSchema = z.enum(TENANT_SIGN_IN_PROVIDERS);
var DEFAULT_TENANT_SIGN_IN_PROVIDER = "google";
var ENTRA_SAML_FIREBASE_PROVIDER_ID = "saml.entra";
function entraSamlIdpEndpoints(directoryId) {
  const id = directoryId.toLowerCase();
  return {
    idpEntityId: `https://sts.windows.net/${id}/`,
    ssoUrl: `https://login.microsoftonline.com/${id}/saml2`
  };
}
__name(entraSamlIdpEndpoints, "entraSamlIdpEndpoints");
var FIREBASE_AUTH_DOMAIN_PATTERN = /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/i;
var FirebaseAuthDomainSchema = z.string().min(1).refine((value) => FIREBASE_AUTH_DOMAIN_PATTERN.test(value), {
  message: "must be a bare hostname (e.g. my-project.firebaseapp.com) \u2014 no scheme, port, path or wildcard"
});
function entraSamlServiceProvider(authDomain) {
  return {
    spEntityId: `https://${authDomain}`,
    acsUrl: `https://${authDomain}/__/auth/handler`
  };
}
__name(entraSamlServiceProvider, "entraSamlServiceProvider");
var WidgetPositionOverrideSchema = z.object({
  row: z.number().int().positive().optional(),
  column: z.number().int().positive().optional(),
  colSpan: z.number().int().positive().optional(),
  rowSpan: z.number().int().positive().optional()
}).strict();
var WidgetOverrideSchema = z.object({
  visible: z.boolean().optional(),
  position: WidgetPositionOverrideSchema.optional()
}).strict();
var DashboardSettingsSchema = z.object({
  widgets: z.record(z.string(), WidgetOverrideSchema).optional()
}).strict();
var ChangeDirection = {
  POSITIVE: "positive",
  NEGATIVE: "negative",
  NEUTRAL: "neutral"
};
var CHANGE_DIRECTION_VALUES = Object.values(ChangeDirection);
function toChangeDirection(change) {
  if (change > 0) return ChangeDirection.POSITIVE;
  if (change < 0) return ChangeDirection.NEGATIVE;
  return ChangeDirection.NEUTRAL;
}
__name(toChangeDirection, "toChangeDirection");
var SERVICE_REGISTRY = [
  {
    id: "office365"
  },
  {
    id: "google-workspace"
  },
  {
    id: "sharepoint"
  }
];
var CONNECTED_SERVICE_VALUES = SERVICE_REGISTRY.map((s) => s.id);
if (CONNECTED_SERVICE_VALUES.length === 0) {
  throw new Error("SERVICE_REGISTRY must have at least one entry");
}
var ConnectedServiceSchema = z.enum(CONNECTED_SERVICE_VALUES);
var ConnectedServicesSchema = z.array(ConnectedServiceSchema);
var ALL_SERVICES = "all";
var SERVICE_FILTER_VALUES = [
  ALL_SERVICES,
  ...CONNECTED_SERVICE_VALUES
];
var SERVICE_FILTER_DEFAULT = ALL_SERVICES;
var DAYS_FILTER_VALUES = [
  1,
  7,
  14,
  30,
  60,
  90,
  180
];
var DAYS_FILTER_DEFAULT = 7;
var TIME_FRAME_PARAMS = DAYS_FILTER_VALUES.map(String);
var CONNECTED_SERVICE_IDS = CONNECTED_SERVICE_VALUES;
function isTimeFrameParam(value) {
  return value != null && TIME_FRAME_PARAMS.includes(value);
}
__name(isTimeFrameParam, "isTimeFrameParam");
function isConnectedService(value) {
  return value != null && CONNECTED_SERVICE_IDS.includes(value);
}
__name(isConnectedService, "isConnectedService");
function effectiveService(stored, connected) {
  if (stored === void 0 || stored === ALL_SERVICES) return stored;
  return isConnectedService(stored) && connected.includes(stored) ? stored : ALL_SERVICES;
}
__name(effectiveService, "effectiveService");
function isServiceOffered(value, connected) {
  return !isConnectedService(value) || connected.includes(value);
}
__name(isServiceOffered, "isServiceOffered");
var DAYS_FILTER_OPTIONS = [
  {
    value: 1,
    label: "Last Day"
  },
  {
    value: 7,
    label: "Last 7 Days"
  },
  {
    value: 14,
    label: "Last 14 Days"
  },
  {
    value: 30,
    label: "Last 30 Days"
  },
  {
    value: 60,
    label: "Last 60 Days"
  },
  {
    value: 90,
    label: "Last 90 Days"
  },
  {
    value: 180,
    label: "Last 180 Days"
  }
];
var SIZE_UNITS = [
  "GB",
  "TB",
  "PB"
];
var BYTES_PER_GB = 1e9;
function bytesToSize(bytes) {
  const gb = bytes / BYTES_PER_GB;
  if (gb >= 1e6) return {
    sizeValue: Math.round(gb / 1e6 * 100) / 100,
    sizeUnit: "PB"
  };
  if (gb >= 1e3) return {
    sizeValue: Math.round(gb / 1e3 * 100) / 100,
    sizeUnit: "TB"
  };
  return {
    sizeValue: Math.round(gb * 10) / 10,
    sizeUnit: "GB"
  };
}
__name(bytesToSize, "bytesToSize");
var TOP_DATA_TAB_IDS = [
  "all",
  "public",
  "internal",
  "confidential",
  "restricted",
  "highly-confidential"
];
var SENSITIVITY_TIERS = [
  {
    id: "highly-confidential",
    label: "Highly Confidential",
    level: "highly confidential",
    fillColor: "#F47600",
    foregroundColor: "#FFFFFF"
  },
  {
    id: "restricted",
    label: "Restricted",
    level: "restricted",
    fillColor: "#FF9700",
    foregroundColor: "#FFFFFF"
  },
  {
    id: "confidential",
    label: "Confidential",
    level: "confidential",
    fillColor: "#FFBB60",
    foregroundColor: "#1B1B1B"
  },
  {
    id: "internal",
    label: "Internal",
    level: "internal",
    fillColor: "#FFDBA9",
    foregroundColor: "#1B1B1B"
  },
  {
    id: "public",
    label: "Public",
    level: "public",
    fillColor: "#FFEACE",
    foregroundColor: "#1B1B1B"
  }
];
var SENSITIVITY_EXTERNAL_COLOR = "#F44236";
var SENSITIVITY_UNCLASSIFIED_COLOR = "#8D8D8D";
var SENSITIVITY_UNCLASSIFIED_ID = "unclassified";
var SENSITIVITY_UNCLASSIFIED_LABEL = "Unclassified";
var SENSITIVITY_UNCLASSIFIED_LEVEL = "unclassified";
function sensitivityLevelFor(id) {
  if (id === SENSITIVITY_UNCLASSIFIED_ID) return SENSITIVITY_UNCLASSIFIED_LEVEL;
  return SENSITIVITY_TIERS.find((tier) => tier.id === id)?.level;
}
__name(sensitivityLevelFor, "sensitivityLevelFor");
new Map([
  ...SENSITIVITY_TIERS.map((tier, i) => [
    tier.id,
    i
  ]),
  [
    SENSITIVITY_UNCLASSIFIED_ID,
    SENSITIVITY_TIERS.length
  ]
]);
function sensitivityLevelsFor(ids) {
  return ids.map((id) => {
    const tier = SENSITIVITY_TIERS.find((candidate) => candidate.id === id);
    if (!tier) throw new Error(`Unknown sensitivity tier: ${id}`);
    return tier.level;
  });
}
__name(sensitivityLevelsFor, "sensitivityLevelsFor");
var DashboardFeatureFlagsSchema = z.object({
  /** Overview page, the landing route. On by default; Firestore `false` hides it (TDEV-1219). */
  overview: z.boolean().default(true),
  /** Which Overview page `/` renders: v1 (off) or v2 (on). Needs `overview` on to show either. */
  overviewV2: z.boolean().default(false)
});
var DashboardFeatureFlagOverridesSchema = z.record(z.string(), DashboardFeatureFlagsSchema.partial());
function resolveFeatureFlags(flags, overrides, tenantId) {
  const override = tenantId === void 0 ? void 0 : overrides[tenantId];
  return override ? {
    ...flags,
    ...override
  } : flags;
}
__name(resolveFeatureFlags, "resolveFeatureFlags");

// src/sensitivity.ts
var SENSITIVITY_LEVELS = [
  "public",
  "internal",
  "confidential",
  "restricted",
  "highly-confidential"
];
var SENSITIVITY_ORDINALS = {
  public: 1,
  internal: 2,
  confidential: 3,
  restricted: 4,
  "highly-confidential": 5
};

// src/regions.ts
var REGION_VALUES = [
  "us",
  "eu"
];
var EU_COUNTRY_CODES = /* @__PURE__ */ new Set([
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  "GB"
]);
function defaultRegionForCountry(country) {
  return EU_COUNTRY_CODES.has(country.toUpperCase()) ? "eu" : "us";
}
__name(defaultRegionForCountry, "defaultRegionForCountry");
function regionFromGcpLocation(location) {
  return location.toLowerCase().startsWith("europe") ? "eu" : "us";
}
__name(regionFromGcpLocation, "regionFromGcpLocation");
var BACKFILL_OPTIONS = [
  {
    days: 3,
    label: "3 days"
  },
  {
    days: 30,
    label: "30 days"
  },
  {
    days: 90,
    label: "3 months"
  },
  {
    days: 180,
    label: "6 months"
  },
  {
    days: 365,
    label: "1 year"
  }
];
var BACKFILL_DAYS_VALUES = BACKFILL_OPTIONS.map((o) => o.days);
var DEFAULT_BACKFILL_DAYS = 180;
var MAX_BACKFILL_DAYS = 730;
var BackfillDaysSchema = z.number().int().positive().max(MAX_BACKFILL_DAYS);

// src/access-requests.ts
var ACCESS_REQUEST_STATUS_VALUES = [
  "pending",
  "approved",
  "rejected",
  "revoked"
];
var ACCESS_REQUEST_EDITABLE_STATUSES = [
  "pending",
  "revoked"
];
var ACCESS_REQUEST_SOURCE_VALUES = [
  "public",
  "manual"
];
var TenantSettingsSchema = z.object({
  theme: z.enum([
    "light",
    "dark"
  ]).default("light"),
  dashboard: DashboardSettingsSchema.optional(),
  connectedServices: ConnectedServicesSchema.default([])
}).strict();
var PartialTenantSettingsSchema = TenantSettingsSchema.partial().strict();
var FILTER_KEY_MAX = 64;
var FILTER_VALUE_MAX = 128;
var FILTER_RECORD_MAX_ENTRIES = 32;
var WIDGET_FILTERS_MAX_WIDGETS = 64;
var BoundedFilterKey = z.string().min(1).max(FILTER_KEY_MAX);
var BoundedFilterValue = z.string().max(FILTER_VALUE_MAX);
var GlobalFilterStateSchema = z.record(BoundedFilterKey, BoundedFilterValue).refine((obj) => Object.keys(obj).length <= FILTER_RECORD_MAX_ENTRIES, {
  message: `globalFilters must have at most ${FILTER_RECORD_MAX_ENTRIES} entries`
});
var WidgetFilterStateSchema = z.record(BoundedFilterKey, z.record(BoundedFilterKey, BoundedFilterValue).refine((obj) => Object.keys(obj).length <= FILTER_RECORD_MAX_ENTRIES, {
  message: `widget filter record must have at most ${FILTER_RECORD_MAX_ENTRIES} entries`
})).refine((obj) => Object.keys(obj).length <= WIDGET_FILTERS_MAX_WIDGETS, {
  message: `widgetFilters must cover at most ${WIDGET_FILTERS_MAX_WIDGETS} widgets`
});
var SidebarStateSchema = z.object({
  isOpen: z.boolean().default(true)
}).strict();
var AdminSettingsSchema = z.object({
  // Override keys (mirror tenant schema). Optional — absence means
  // "fall through to tenant or application default".
  theme: z.enum([
    "light",
    "dark"
  ]).optional(),
  dashboard: DashboardSettingsSchema.optional(),
  // Admin-only keys. Defaulted to {} so parse({}) populates the inner
  // schema defaults (e.g. sidebar.isOpen=true).
  globalFilters: GlobalFilterStateSchema.default({}),
  widgetFilters: WidgetFilterStateSchema.default({}),
  sidebar: SidebarStateSchema.default({})
}).strict();
var PartialAdminSettingsSchema = AdminSettingsSchema.partial().strict();
var EffectiveSettingsSchema = TenantSettingsSchema.extend({
  // Admin-only keys folded into the effective shape.
  globalFilters: GlobalFilterStateSchema.default({}),
  widgetFilters: WidgetFilterStateSchema.default({}),
  sidebar: SidebarStateSchema.default({})
}).strict();
var ACCESS_LEGITIMACY_SORT_DIR_VALUES = [
  "asc",
  "desc"
];
var ACCESS_LEGITIMACY_PAGE_SIZE = 20;
var ACCESS_LEGITIMACY_SCOPE_CONTRACT = 2;
var ACCESS_LEGITIMACY_SCOPE_CONTRACT_HEADER = "x-dawn-scope-contract";
var ACCESS_LEGITIMACY_CHART_AS_OF_HEADER = "x-dawn-chart-as-of";
var ACCESS_LEGITIMACY_ANALYSED_ONLY_HEADER = "x-dawn-analysed-only";
var TIMESTAMP_MIN_MS = Date.parse("0001-01-01T00:00:00Z");
var TIMESTAMP_MAX_MS = Date.parse("9999-12-31T23:59:59.999Z");
var OFFSET_MAX_MINUTES = 14 * 60;
var MINUTES_PER_HOUR = 60;
var WITH_SECONDS = /T\d{2}:\d{2}:\d{2}/;
var FINER_THAN_MICROSECONDS = /\.\d{7}/;
var OFFSET = /[+-](\d{2}):?(\d{2})$/;
var parsesInBigQuery = /* @__PURE__ */ __name((value) => {
  if (!WITH_SECONDS.test(value) || FINER_THAN_MICROSECONDS.test(value)) return false;
  const offset = OFFSET.exec(value);
  if (offset) {
    const minutes = Number(offset[2]);
    const total = Number(offset[1]) * MINUTES_PER_HOUR + minutes;
    if (minutes >= MINUTES_PER_HOUR || total > OFFSET_MAX_MINUTES) return false;
  }
  const instant = Date.parse(value);
  return instant >= TIMESTAMP_MIN_MS && instant <= TIMESTAMP_MAX_MS;
}, "parsesInBigQuery");
var CHART_AS_OF = z.string().datetime({
  offset: true
}).refine(parsesInBigQuery);
var isChartAsOf = /* @__PURE__ */ __name((value) => CHART_AS_OF.safeParse(value).success, "isChartAsOf");
var ACCESS_SCOPE_STALE = "ACCESS_SCOPE_STALE";
var OnboardingFeatureFlagsSchema = z.record(z.string(), z.boolean());
var RATE_LIMIT_MAX_LIMIT = 1e4;
var RATE_LIMIT_MAX_TTL = 864e5;
var onboardingRateLimitTier = /* @__PURE__ */ __name((ttl, limit) => z.object({
  ttl: z.coerce.number().int().min(1e3).transform((v) => Math.min(v, RATE_LIMIT_MAX_TTL)).default(ttl),
  limit: z.coerce.number().int().min(1).transform((v) => Math.min(v, RATE_LIMIT_MAX_LIMIT)).default(limit)
}).default({
  ttl,
  limit
}), "onboardingRateLimitTier");
var OnboardingRateLimitTiersSchema = z.object({
  default: onboardingRateLimitTier(6e4, 100),
  resolve: onboardingRateLimitTier(6e4, 10),
  requestCode: onboardingRateLimitTier(6e4, 5),
  verifyCode: onboardingRateLimitTier(6e4, 10),
  // Connect-data verify endpoint (TDEV-146). Each submit fans out to Google +
  // Microsoft — an amplification surface the `default` tier isn't sized for.
  connectDataSubmit: onboardingRateLimitTier(6e4, 5),
  // Entra sign-in validate endpoint (TDEV-156). Two public Microsoft fetches
  // per call; keyed per invitation session so one link holder cannot fan out
  // from rotating IPs while a NAT'd office shares nothing (umbrella D28).
  signInValidate: onboardingRateLimitTier(6e4, 5)
}).default({});
var ONBOARDING_SETUP_STATUSES = [
  "provisioning",
  "analyzing_events",
  "completed",
  "failed"
];
var OnboardingSetupStatusSchema = z.enum(ONBOARDING_SETUP_STATUSES);
var OnboardingSetupStatusResponseSchema = z.object({
  status: OnboardingSetupStatusSchema,
  email: z.string().email()
});
var OnboardingFeatureConfigSchema = z.object({
  featureFlags: OnboardingFeatureFlagsSchema.default({}),
  rateLimitTiers: OnboardingRateLimitTiersSchema,
  supportEmail: z.string().email().default("support@dawnsecurity.io"),
  // Opt-in for finish to self-publish core-tenant-ready right after its
  // core-tenant-create (TDEV-1187), for a deployment with no core2 to send that
  // ack (the demo environment). Ops flips it via PATCH /internal/config/onboarding.
  // Takes effect ONLY on an allowlisted tier — `autoAcksCoreReady` in
  // tenant-lifecycle-api ANDs it with `isDevOrSandboxEnvironment`, so a stray
  // `true` on staging or prod changes nothing. Server-side only.
  autoAckCoreReady: z.boolean().default(false)
});
var OnboardingSessionResponseSchema = z.object({
  inactivityTimeoutSeconds: z.number().int().positive(),
  inactivityWarningOffsetSeconds: z.number().int().nonnegative(),
  maxDurationSeconds: z.number().int().positive(),
  maxDurationWarningOffsetSeconds: z.number().int().nonnegative(),
  autoRedirectSeconds: z.number().int().nonnegative(),
  pingIntervalSeconds: z.number().int().positive()
});
var OnboardingConnectDataGoogleSchema = z.object({
  subject: z.string().email()
}).strict();
var OnboardingConnectDataM365Schema = z.object({
  azureTenantId: z.string().uuid(),
  clientId: z.string().uuid()
}).strict();
var ONBOARDING_HIBOB_API_BASE_URLS = [
  "https://api.hibob.com/v1",
  "https://api.sandbox.hibob.com/v1"
];
var OnboardingHibobApiBaseUrlSchema = z.enum(ONBOARDING_HIBOB_API_BASE_URLS);
var OnboardingConnectDataHibobSchema = z.object({
  apiUrl: OnboardingHibobApiBaseUrlSchema,
  serviceUserId: z.string().min(1),
  token: z.string().min(1)
}).strict();
var OnboardingConnectDataHibobSavedSchema = z.object({
  apiUrl: OnboardingHibobApiBaseUrlSchema,
  serviceUserId: z.string().min(1),
  verified: z.boolean().optional()
}).strict();
var OnboardingEntraSignInIdsSchema = z.object({
  directoryId: z.string().uuid(),
  applicationId: z.string().uuid()
}).strict();
var OnboardingConnectDataPrefillSchema = z.object({
  google: OnboardingConnectDataGoogleSchema,
  m365: OnboardingConnectDataM365Schema,
  /**
  * HiBob demo pre-fill (TDEV-942 follow-up) — OPTIONAL where the wire pair is
  * required: hibob joined demo mode later, so a deployment configured with only
  * the wire values keeps working (its hibob rows behave non-demo).
  *
  * The token IS included, deliberately. The write-only rule protects CUSTOMER
  * tokens (Dawn must never become a second place to read one back from); this
  * one is Dawn's own sandbox service-user token, deployment configuration
  * handed to demo users on purpose so the row works without a Bob login. It
  * rides only the authenticated config response, is pinned server-side like
  * every other demo value, and still never enters saved progress — the saved
  * shape has no token key regardless of where the value came from.
  */
  hibob: OnboardingConnectDataHibobSchema.optional(),
  /**
  * Entra sign-in pre-fill (TDEV-156 follow-up): the two GUIDs the Entra
  * Connect dialog opens with on a demo invitation — the demo directory and the
  * deployment's demo SAML enterprise application. OPTIONAL like hibob: it
  * joined demo mode after wire-only deployments existed, and a sandbox with no
  * demo SAML application simply has the admin type the ids (D20 revised).
  *
  * Unlike the three authorization values, it is NOT pinned server-side:
  * validating against an arbitrary enterprise application is exactly what
  * every non-demo invitation may do, and the sign-in trust anchor it produces
  * is the demo tenant's own — no standing Dawn grant is reachable through it.
  */
  entraSignIn: OnboardingEntraSignInIdsSchema.optional()
});
var OnboardingConnectDataSchema = z.object({
  google: z.object({
    serviceAccountEmail: z.string(),
    /** The SA's oauth2ClientId — the "Client ID" in the DWD authorization form. */
    clientId: z.string(),
    scopes: z.array(z.string())
  }),
  m365: z.object({
    serviceAccountEmail: z.string(),
    /** The FIC "Subject": the SA's numeric uniqueId (NOT its email). */
    subject: z.string(),
    issuer: z.string(),
    audience: z.string(),
    graphPermissions: z.array(z.string()),
    managementApiPermissions: z.array(z.string())
  }),
  /**
  * Demo mode (TDEV-778). Present ONLY when the invitation's persisted
  * `provider_service_accounts` rows are BOTH the deployment's configured demo
  * accounts (D21) — never from the invitation's flag alone, in any ordering of
  * provisioning and configuration. Absent is the normal case and needs no
  * interpretation; presence is the sole demo signal the frontend gets (D7).
  */
  prefill: OnboardingConnectDataPrefillSchema.optional()
});
var OnboardingEntraSamlServiceProviderSchema = z.object({
  spEntityId: z.string().url(),
  acsUrl: z.string().url()
});
var OnboardingConfigResponseSchema = z.object({
  environment: z.string(),
  featureFlags: OnboardingFeatureFlagsSchema,
  session: OnboardingSessionResponseSchema,
  connectData: OnboardingConnectDataSchema.optional(),
  // Sign-in provider setup values (TDEV-156). Required and unauthenticated —
  // the setup instructions render them before any wizard state exists.
  signIn: z.object({
    entraSaml: OnboardingEntraSamlServiceProviderSchema
  }),
  // Public support address (not a secret) — rides the unauthenticated base
  // response like `session` does. `.email()`-validated as the trust boundary
  // for the web's `mailto:` link (§7 S5); never relax to bare `z.string()`.
  supportEmail: z.string().email(),
  // The deployment's data region, rendered read-only on the sign-in step. Sent as
  // the region CODE, not a label: the copy is the web's business. Required, and on
  // the unauthenticated base response — the sign-in step needs it, and it says
  // nothing a caller could not infer from which regional URL they just loaded.
  dataRegion: z.enum(REGION_VALUES)
});
var ONBOARDING_GUIDE_SECTIONS = {
  entraSignIn: "5-1-microsoft-entra-id-sign-in",
  microsoft365EntraId: "71-microsoft-365-entra-id",
  googleWorkspace: "72-google-workspace",
  hibob: "73-hibob-optional"
};
var ONBOARDING_WIZARD_STEP_IDS = [
  "sign-in",
  "connect-data",
  "identity-provider",
  "hr-systems",
  "finish"
];
var OnboardingWizardStepIdSchema = z.enum(ONBOARDING_WIZARD_STEP_IDS);
var ONBOARDING_SUPPORTED_SSO_PROVIDERS = [
  "google",
  "microsoft"
];
var OnboardingSsoProviderSchema = z.enum(ONBOARDING_SUPPORTED_SSO_PROVIDERS);
var ONBOARDING_DATA_SOURCES = [
  "microsoft",
  "google"
];
var OnboardingDataSourceSchema = z.enum(ONBOARDING_DATA_SOURCES);
var ONBOARDING_ENV_IDPS = [
  "entra-id",
  "google",
  "okta"
];
var OnboardingEnvIdpSchema = z.enum(ONBOARDING_ENV_IDPS);
var ONBOARDING_ENV_WORKSPACES = [
  "m365",
  "google-workspace"
];
var OnboardingEnvWorkspaceSchema = z.enum(ONBOARDING_ENV_WORKSPACES);
var ONBOARDING_ENV_HR_SYSTEMS = [
  "hibob",
  "workday"
];
var OnboardingEnvHrSystemSchema = z.enum(ONBOARDING_ENV_HR_SYSTEMS);
var OnboardingEnvironmentSchema = z.union([
  z.object({
    idp: OnboardingEnvIdpSchema,
    workspace: OnboardingEnvWorkspaceSchema,
    hrSystem: OnboardingEnvHrSystemSchema.optional()
  }).strict(),
  z.object({
    idp: OnboardingEnvIdpSchema,
    workspaces: z.array(OnboardingEnvWorkspaceSchema).min(1).max(ONBOARDING_ENV_WORKSPACES.length).refine((v) => new Set(v).size === v.length, "workspaces must be unique"),
    hrSystem: OnboardingEnvHrSystemSchema.optional()
  }).strict()
]).transform((env) => ({
  idp: env.idp,
  workspaces: "workspace" in env ? [
    env.workspace
  ] : env.workspaces,
  ...env.hrSystem !== void 0 && {
    hrSystem: env.hrSystem
  }
}));
var ONBOARDING_PROVIDER_ROLES = [
  "idp",
  "workspace"
];
var OnboardingProviderRoleSchema = z.enum(ONBOARDING_PROVIDER_ROLES);
var ONBOARDING_GOOGLE_SCOPES_BY_ROLE = {
  idp: [
    "https://www.googleapis.com/auth/admin.directory.user.readonly",
    "https://www.googleapis.com/auth/admin.directory.group.readonly",
    "https://www.googleapis.com/auth/admin.directory.group.member.readonly",
    "https://www.googleapis.com/auth/admin.reports.audit.readonly"
  ],
  workspace: [
    "https://www.googleapis.com/auth/drive.readonly",
    "https://www.googleapis.com/auth/admin.reports.audit.readonly"
  ]
};
var ONBOARDING_M365_GRAPH_PERMISSIONS_BY_ROLE = {
  idp: [
    "User.Read.All",
    "Group.Read.All",
    "GroupMember.Read.All",
    "Directory.Read.All",
    "AuditLog.Read.All"
  ],
  workspace: [
    "Sites.Read.All",
    "Files.Read.All",
    "AuditLogsQuery-OneDrive.Read.All",
    "AuditLogsQuery-SharePoint.Read.All"
  ]
};
var ONBOARDING_M365_MGMT_PERMISSIONS_BY_ROLE = {
  idp: [],
  workspace: [
    "ActivityFeed.Read"
  ]
};
function onboardingPermissionsForRoles(byRole, roles) {
  return [
    ...new Set(roles.flatMap((role) => byRole[role]))
  ];
}
__name(onboardingPermissionsForRoles, "onboardingPermissionsForRoles");
function onboardingProviderRolesFor(environment) {
  const rolesFor = /* @__PURE__ */ __name((idp, workspace) => {
    const hasWorkspace = environment.workspaces.includes(workspace);
    const hasIdp = environment.idp === idp || hasWorkspace;
    return [
      ...hasIdp ? [
        "idp"
      ] : [],
      ...hasWorkspace ? [
        "workspace"
      ] : []
    ];
  }, "rolesFor");
  return {
    google: rolesFor("google", "google-workspace"),
    m365: rolesFor("entra-id", "m365")
  };
}
__name(onboardingProviderRolesFor, "onboardingProviderRolesFor");
var OnboardingProgressDataSchema = z.object({
  // `entra` is the client's resume affordance for the Entra tile (TDEV-156,
  // umbrella D4): the GUIDs it typed and whether the dialog showed verified.
  // It is NOT what the server trusts — the validated SAML config lives in the
  // server-written `sign_in_config` column, and GET /progress reports the
  // server-derived `signIn.entraVerified` (config present AND its GUIDs equal
  // these). A forged `verified: true` here changes nothing.
  signIn: z.object({
    ssoProvider: OnboardingSsoProviderSchema,
    entra: OnboardingEntraSignInIdsSchema.extend({
      verified: z.boolean().optional()
    }).strict().optional()
  }).strict().optional(),
  // The define-environment selection (TDEV-637). Supersedes `connectData`
  // below as step 2's payload; connectData stays for rows saved by the old
  // step and for the not-yet-reworked setup screens that read it.
  environment: OnboardingEnvironmentSchema.optional(),
  connectData: z.object({
    sources: z.array(OnboardingDataSourceSchema).min(1).max(ONBOARDING_DATA_SOURCES.length).refine((v) => new Set(v).size === v.length, "sources must be unique"),
    // The customer's return values, saved by the FE on every connect-data
    // Continue (TDEV-146) — verified or not, so a refresh never loses typed
    // values. Identifiers, not secrets — the assembled provider secret lives
    // only in Secret Manager. `verified` marks a provider whose probe passed
    // (the resume fast-path affordance); absent = verified, because legacy
    // rows were only ever written after a fully-verified submit. Keys are
    // tied to `sources` below.
    google: OnboardingConnectDataGoogleSchema.extend({
      verified: z.boolean().optional()
    }).strict().optional(),
    m365: OnboardingConnectDataM365Schema.extend({
      verified: z.boolean().optional()
    }).strict().optional(),
    // The optional HiBob HR row (TDEV-942). Deliberately NOT an entry in
    // `sources`: sources gate the finish step (every source must verify)
    // and become the core leg's `providers`, while HiBob is optional and
    // its core hand-off is a separate scope. Tied to the environment's
    // hrSystem pick instead (superRefine below). Token-free by schema.
    hibob: OnboardingConnectDataHibobSavedSchema.optional()
  }).strict().optional(),
  identityProvider: z.object({
    defaultProvider: OnboardingDataSourceSchema
  }).strict().optional()
}).strict().superRefine((data, ctx) => {
  if (data.identityProvider && !data.connectData?.sources.includes(data.identityProvider.defaultProvider)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [
        "identityProvider",
        "defaultProvider"
      ],
      message: "defaultProvider must be one of the connected sources"
    });
  }
  const sources = data.connectData?.sources ?? [];
  if (data.connectData?.google && !sources.includes("google")) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [
        "connectData",
        "google"
      ],
      message: "google identifiers require google in sources"
    });
  }
  if (data.connectData?.m365 && !sources.includes("microsoft")) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [
        "connectData",
        "m365"
      ],
      message: "m365 identifiers require microsoft in sources"
    });
  }
  if (data.connectData?.hibob && data.environment?.hrSystem !== "hibob") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [
        "connectData",
        "hibob"
      ],
      message: "hibob identifiers require hibob as the environment hrSystem"
    });
  }
});
var OnboardingProgressSchema = z.object({
  currentStep: OnboardingWizardStepIdSchema,
  data: OnboardingProgressDataSchema
});
var OnboardingProgressResponseSchema = z.object({
  progress: OnboardingProgressSchema.nullable(),
  signIn: z.object({
    entraVerified: z.boolean()
  })
});
var ONBOARDING_PROBE_FAILURE_REASONS = [
  "dwd_not_authorized",
  "subject_not_found",
  "fic_mismatch",
  "permissions_not_consented",
  "hibob_auth_failed",
  "hibob_permissions_missing",
  "demo_value_pinned",
  "entra_directory_not_found",
  "entra_app_not_found",
  "entra_metadata_invalid",
  "provider_unreachable",
  "internal_error",
  "secret_write_failed"
];
var OnboardingProbeFailureReasonSchema = z.enum(ONBOARDING_PROBE_FAILURE_REASONS);
var OnboardingProviderOutcomeSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("verified")
  }),
  z.object({
    status: z.literal("failed"),
    reason: OnboardingProbeFailureReasonSchema
  })
]);
var OnboardingEntraSignInValidateSchema = OnboardingEntraSignInIdsSchema;
var OnboardingEntraSignInValidateResponseSchema = OnboardingProviderOutcomeSchema;
var ONBOARDING_FINISH_CONFLICT_CODES = [
  "sign_in_not_validated"
];
var OnboardingFinishConflictCodeSchema = z.enum(ONBOARDING_FINISH_CONFLICT_CODES);
var OnboardingFinishConflictBodySchema = z.object({
  code: OnboardingFinishConflictCodeSchema,
  message: z.string()
});
var ONBOARDING_CONNECT_SUBMIT_SOURCES = [
  ...ONBOARDING_DATA_SOURCES,
  "hibob"
];
var OnboardingConnectSubmitSourceSchema = z.enum(ONBOARDING_CONNECT_SUBMIT_SOURCES);
var OnboardingConnectDataSubmitSchema = z.object({
  sources: z.array(OnboardingConnectSubmitSourceSchema).min(1).max(ONBOARDING_CONNECT_SUBMIT_SOURCES.length).refine((v) => new Set(v).size === v.length, "sources must be unique"),
  google: OnboardingConnectDataGoogleSchema.optional(),
  m365: OnboardingConnectDataM365Schema.optional(),
  hibob: OnboardingConnectDataHibobSchema.optional()
}).strict().superRefine((data, ctx) => {
  const hasGoogleSource = data.sources.includes("google");
  const hasMicrosoftSource = data.sources.includes("microsoft");
  const hasHibobSource = data.sources.includes("hibob");
  if (hasGoogleSource !== !!data.google) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [
        "google"
      ],
      message: "google payload must be present iff google is a selected source"
    });
  }
  if (hasMicrosoftSource !== !!data.m365) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [
        "m365"
      ],
      message: "m365 payload must be present iff microsoft is a selected source"
    });
  }
  if (hasHibobSource !== !!data.hibob) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [
        "hibob"
      ],
      message: "hibob payload must be present iff hibob is a selected source"
    });
  }
});
var OnboardingConnectDataSubmitResponseSchema = z.object({
  google: OnboardingProviderOutcomeSchema.optional(),
  m365: OnboardingProviderOutcomeSchema.optional(),
  hibob: OnboardingProviderOutcomeSchema.optional()
});
var TENANT_SOURCE_KEYS = [
  "gdrive",
  "onedrive",
  "sharepoint"
];
var DRIVE_ENTRY_MAX_LENGTH = 2048;
var DRIVE_LIST_MAX_ENTRIES = 100;
var DriveEntry = z.string().trim().min(1).max(DRIVE_ENTRY_MAX_LENGTH);
var DriveLists = z.object({
  drives: z.object({
    allow: z.array(DriveEntry).max(DRIVE_LIST_MAX_ENTRIES).optional(),
    deny: z.array(DriveEntry).max(DRIVE_LIST_MAX_ENTRIES).optional()
  }).strict()
}).strict();
var TenantSourcesSchema = z.object({
  gdrive: DriveLists.optional(),
  onedrive: DriveLists.optional(),
  sharepoint: DriveLists.optional()
}).strict().refine((s) => Object.values(s).some((lists) => lists !== void 0), "sources must name at least one source");
var SCOPE_MODES = [
  "all",
  "allow",
  "deny"
];
function scopeModeFor(lists) {
  if ((lists?.drives.allow?.length ?? 0) > 0) return "allow";
  if ((lists?.drives.deny?.length ?? 0) > 0) return "deny";
  return "all";
}
__name(scopeModeFor, "scopeModeFor");
function sourceHasSingleMode(lists) {
  const hasAllow = (lists?.drives.allow?.length ?? 0) > 0;
  const hasDeny = (lists?.drives.deny?.length ?? 0) > 0;
  return !(hasAllow && hasDeny);
}
__name(sourceHasSingleMode, "sourceHasSingleMode");
function scopeIsConsoleEditable(sources) {
  if (!sources) return true;
  return TENANT_SOURCE_KEYS.every((key) => sourceHasSingleMode(sources[key]));
}
__name(scopeIsConsoleEditable, "scopeIsConsoleEditable");
var ScopedTenantSourcesSchema = TenantSourcesSchema.superRefine((sources, ctx) => {
  for (const key of TENANT_SOURCE_KEYS) {
    if (!sourceHasSingleMode(sources[key])) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [
          key,
          "drives"
        ],
        message: `${key}: set an allow list or a deny list, not both`
      });
    }
  }
});
function sameDriveLists(a, b) {
  if (!a) {
    return false;
  }
  const canonical = /* @__PURE__ */ __name((s) => JSON.stringify(TENANT_SOURCE_KEYS.map((key) => [
    key,
    [
      ...s[key]?.drives.allow ?? []
    ].sort(),
    [
      ...s[key]?.drives.deny ?? []
    ].sort()
  ])), "canonical");
  return canonical(a) === canonical(b);
}
__name(sameDriveLists, "sameDriveLists");
var EMPTY_DRIVES_SOURCES = {
  sharepoint: {
    drives: {
      allow: [
        "https://dsecaitest.sharepoint.com/sites/ca0e0ee8-sandbox"
      ]
    }
  },
  gdrive: {
    drives: {
      allow: [
        "0AJefUMha6Ww6Uk9PVA"
      ]
    }
  },
  onedrive: {
    drives: {
      allow: [
        "disabled"
      ]
    }
  }
};
function demoFlipSources(turningOn, current) {
  if (turningOn) return current ?? EMPTY_DRIVES_SOURCES;
  return sameDriveLists(current, EMPTY_DRIVES_SOURCES) ? null : current;
}
__name(demoFlipSources, "demoFlipSources");
var PROVISIONING_TOPICS = {
  coreTenantCreate: "core-tenant-create",
  coreTenantReady: "core-tenant-ready",
  dashboardTenantCreate: "dashboard-tenant-create",
  dashboardTenantReady: "dashboard-tenant-ready"
};
var PROVISIONING_SUBSCRIPTIONS = {
  coreTenantReady: "core-tenant-ready-sub",
  dashboardTenantCreate: "dashboard-tenant-create-sub",
  dashboardTenantReady: "dashboard-tenant-ready-sub"
};
var TenantCreateBaseSchema = z.object({
  tenantId: z.string().uuid(),
  email: z.string().email(),
  companyName: z.string().min(1)
});
var SAML_CERTIFICATE_MAX_BYTES = 4096;
var SAML_CERTIFICATES_MAX = 5;
var PEM_CERTIFICATE_PATTERN = /^-----BEGIN CERTIFICATE-----\r?\n(?:[A-Za-z0-9+/=]{1,76}\r?\n)+-----END CERTIFICATE-----\r?\n?$/;
var SamlSignInConfigSchema = z.object({
  provider: z.literal("entra-saml"),
  directoryId: z.string().uuid(),
  applicationId: z.string().uuid(),
  idpEntityId: z.string().url(),
  ssoUrl: z.string().url(),
  certificates: z.array(z.string().max(SAML_CERTIFICATE_MAX_BYTES).regex(PEM_CERTIFICATE_PATTERN)).min(1).max(SAML_CERTIFICATES_MAX)
});
var TenantCreateMessageSchema = TenantCreateBaseSchema.extend({
  signIn: SamlSignInConfigSchema.optional()
});
var CoreTenantCreateMessageSchema = TenantCreateBaseSchema.extend({
  providers: z.array(OnboardingDataSourceSchema).min(1).refine((v) => new Set(v).size === v.length, "providers must be unique"),
  defaultProvider: OnboardingDataSourceSchema,
  backfillDays: BackfillDaysSchema,
  hr: z.object({
    provider: z.literal("hibob")
  }).optional(),
  // Per-source drive allow/deny lists core writes into
  // `tenant_settings.settings.sources` (TDEV-1176) — set only for demo
  // invitations. Absent = no restriction; additive, so no schema_version bump.
  sources: TenantSourcesSchema.optional()
}).refine((body) => body.providers.includes(body.defaultProvider), {
  path: [
    "defaultProvider"
  ],
  message: "defaultProvider must be one of providers"
});
var TenantReadyMessageSchema = z.object({
  tenantId: z.string().uuid(),
  status: z.enum([
    "completed",
    "failed"
  ]),
  error: z.string().optional()
});
var PROVISIONING_SCHEMA_VERSION = "1";
var ACCEPTED_SCHEMA_VERSIONS = {
  [PROVISIONING_TOPICS.dashboardTenantCreate]: [
    "1",
    "2"
  ]
};
function provisioningEnvelope(tenantId, eventType, options = {}) {
  return {
    tenant_id: tenantId,
    idempotency_key: tenantId,
    event_type: eventType,
    schema_version: options.schemaVersion ?? PROVISIONING_SCHEMA_VERSION
  };
}
__name(provisioningEnvelope, "provisioningEnvelope");
function provisioningExpectation(eventType, tenantId) {
  return {
    eventType,
    schemaVersion: ACCEPTED_SCHEMA_VERSIONS[eventType] ?? PROVISIONING_SCHEMA_VERSION,
    ...tenantId !== void 0 ? {
      tenantId
    } : {}
  };
}
__name(provisioningExpectation, "provisioningExpectation");
var ONBOARDING_MESSAGING_TOPICS = {
  invitationCreate: "onboarding-invitation-create",
  invitationRevoke: "onboarding-invitation-revoke",
  status: "onboarding-status"
};
var ONBOARDING_MESSAGING_SUBSCRIPTIONS = {
  invitationCreate: "onboarding-invitation-create-sub",
  invitationRevoke: "onboarding-invitation-revoke-sub",
  status: "onboarding-status-sub"
};
var InvitationCreateCommandSchema = z.object({
  tenantId: z.string().uuid(),
  email: z.string().email().max(255),
  name: z.string().min(1).max(255),
  companyName: z.string().min(1).max(255),
  backfillDays: BackfillDaysSchema,
  isDemo: z.boolean().default(false),
  // The container-level scope ops chose at invite (TDEV-1233): per-source
  // allow/deny lists applied at provisioning (`core-tenant-create`). OPTIONAL —
  // absent means "no restriction", today's behaviour. A demo invitation's empty
  // drives arrive here too (ops prefills them); there is no demo fallback.
  sources: TenantSourcesSchema.optional()
});
var InvitationRevokeCommandSchema = z.object({
  tenantId: z.string().uuid()
});
var ONBOARDING_JOURNEY_STATUSES = [
  "invitation_failed",
  "invited",
  "verified",
  "wizard",
  "provisioning_core",
  "provisioning_dashboard",
  "completed",
  "failed",
  "revoked"
];
var NON_REVOCABLE_ONBOARDING_STATUSES = [
  "provisioning_core",
  "provisioning_dashboard",
  "completed",
  "failed"
];
var EXPIRABLE_ONBOARDING_STATUSES = [
  "invited",
  "verified",
  "wizard"
];
function isInvitationExpired(status, expiresAt, now = Date.now()) {
  if (!status || !expiresAt) return false;
  if (!EXPIRABLE_ONBOARDING_STATUSES.includes(status)) return false;
  return new Date(expiresAt).getTime() <= now;
}
__name(isInvitationExpired, "isInvitationExpired");
var OnboardingJourneyStatusMessageSchema = z.object({
  tenantId: z.string().uuid(),
  status: z.enum(ONBOARDING_JOURNEY_STATUSES),
  step: OnboardingWizardStepIdSchema.optional(),
  invitationId: z.string().uuid().optional(),
  expiresAt: z.string().datetime().optional(),
  devLink: z.string().max(2048).optional(),
  error: z.string().max(500).optional(),
  occurredAt: z.string().datetime()
});
var ONBOARDING_MESSAGING_SCHEMA_VERSION = "1";
function onboardingMessagingEnvelope(tenantId, eventType, idempotencyKey) {
  return {
    tenant_id: tenantId,
    idempotency_key: idempotencyKey,
    event_type: eventType,
    schema_version: ONBOARDING_MESSAGING_SCHEMA_VERSION
  };
}
__name(onboardingMessagingEnvelope, "onboardingMessagingEnvelope");
function onboardingMessagingExpectation(eventType, tenantId) {
  return {
    eventType,
    schemaVersion: ONBOARDING_MESSAGING_SCHEMA_VERSION,
    ...tenantId !== void 0 ? {
      tenantId
    } : {}
  };
}
__name(onboardingMessagingExpectation, "onboardingMessagingExpectation");
var TENANT_LIFECYCLE_TOPICS = {
  actions: "tenant-lifecycle-action",
  commands: "core-tenant-lifecycle",
  events: "core-tenant-lifecycle-events",
  // Off-boarding journey snapshot back to operations (TDEV-571 A3). Named
  // symmetric with `onboarding-status` — the snapshot describes the OFF-boarding
  // record; the operations tenants view mirrors it into its own schema and never
  // reads the off-boarding schema directly (one-role-per-schema isolation).
  status: "offboarding-status"
};
var TENANT_LIFECYCLE_SUBSCRIPTIONS = {
  actions: "tenant-lifecycle-action-sub",
  commands: "core-tenant-lifecycle-sub",
  events: "core-tenant-lifecycle-events-sub",
  status: "offboarding-status-sub"
};
var TENANT_LIFECYCLE_SCHEMA_VERSION = "1";
function tenantLifecycleEnvelope(tenantId, action, requestId) {
  return {
    tenant_id: tenantId,
    idempotency_key: `${action}:${tenantId}:${requestId}`,
    event_type: action,
    schema_version: TENANT_LIFECYCLE_SCHEMA_VERSION
  };
}
__name(tenantLifecycleEnvelope, "tenantLifecycleEnvelope");
var TENANT_LIFECYCLE_ACTIONS = [
  "tenant-deactivate",
  "tenant-activate",
  "tenant-hold",
  "tenant-release",
  "tenant-delete"
];
var TenantLifecycleActionSchema = z.enum(TENANT_LIFECYCLE_ACTIONS);
var TENANT_CONFIG_ACTIONS = [
  "tenant-config-update",
  "tenant-prompt-promote",
  "tenant-label-version-update"
];
var TenantConfigActionSchema = z.enum(TENANT_CONFIG_ACTIONS);
var TenantCommandActionSchema = z.union([
  TenantLifecycleActionSchema,
  TenantConfigActionSchema
]);
function isTenantConfigAction(action) {
  return TENANT_CONFIG_ACTIONS.includes(action);
}
__name(isTenantConfigAction, "isTenantConfigAction");
var commandBase = z.object({
  tenantId: z.string().uuid(),
  requestId: z.string().uuid()
});
var TenantDeactivateTriggerSchema = z.enum([
  "customer_request",
  "operations"
]);
var TenantDeleteTriggerSchema = z.enum([
  "verified_request",
  "retention_lapse"
]);
var MAX_RETENTION_DAYS = 3650;
var TenantDeactivateCommandSchema = commandBase.extend({
  trigger: TenantDeactivateTriggerSchema,
  retentionDays: z.number().int().positive().max(MAX_RETENTION_DAYS)
});
var TenantActivateCommandSchema = commandBase;
var TenantHoldCommandSchema = commandBase.extend({
  reason: z.string().min(1).max(1024)
});
var TenantReleaseCommandSchema = commandBase;
var TenantDeleteCommandSchema = commandBase;
var TenantDeactivatedEventSchema = z.object({
  tenantId: z.string().uuid(),
  requestId: z.string().uuid(),
  trigger: TenantDeactivateTriggerSchema,
  deactivatedAt: z.string().datetime({
    offset: true
  }),
  deletionDueAt: z.string().datetime({
    offset: true
  })
});
var TenantActivatedEventSchema = z.object({
  tenantId: z.string().uuid(),
  requestId: z.string().uuid()
});
var TenantLifecycleFailedEventSchema = z.object({
  tenantId: z.string().uuid(),
  requestId: z.string().uuid(),
  // Off-boarding OR config-write action (TDEV-882/883): core raises the same
  // InvalidLifecycleCommandError for both families and publishes this one event.
  action: TenantCommandActionSchema,
  /** Human-readable, log/store only — never shown to the customer. */
  error: z.string()
});
var TenantHeldEventSchema = z.object({
  tenantId: z.string().uuid(),
  requestId: z.string().uuid(),
  reason: z.string(),
  heldAt: z.string().datetime({
    offset: true
  })
});
var TenantReleasedEventSchema = z.object({
  tenantId: z.string().uuid(),
  requestId: z.string().uuid(),
  deletionDueAt: z.string().datetime({
    offset: true
  })
});
var TenantDeleteStartedEventSchema = z.object({
  tenantId: z.string().uuid(),
  requestId: z.string().uuid(),
  trigger: TenantDeleteTriggerSchema,
  deletionDueAt: z.string().datetime({
    offset: true
  }).optional()
});
var TenantErasureEvidenceSchema = z.object({
  store: z.string(),
  itemsDeleted: z.number().int().nonnegative(),
  finishedAt: z.string().datetime({
    offset: true
  }).nullable(),
  verifiedAt: z.string().datetime({
    offset: true
  }).nullable()
});
var TenantDeletedEventSchema = z.object({
  tenantId: z.string().uuid(),
  requestId: z.string().uuid(),
  trigger: TenantDeleteTriggerSchema,
  evidence: z.array(TenantErasureEvidenceSchema)
});
var OFFBOARDING_LIFECYCLE_STATES = [
  "active",
  "deactivated",
  "held",
  "deletion_started",
  "deleted",
  "action_failed"
];
var OffboardingStatusMessageSchema = z.object({
  tenantId: z.string().uuid(),
  /** Derived off-boarding journey state — renders the tenants-view badge with no history. */
  lifecycleState: z.enum(OFFBOARDING_LIFECYCLE_STATES),
  /** The wire action of the record this snapshot describes (audit/last-action column). */
  lastAction: TenantLifecycleActionSchema.optional(),
  /** Status of that action's record (received/sent/confirmed/completed/failed). */
  lastActionStatus: z.string().max(32).optional(),
  /**
  * Deletion provenance (PR 5), present only on a deletion record — lets the ops
  * view label a `retention_lapse` (system-initiated) deletion distinctly from a
  * `verified_request` (ops-ordered) one. Absent for non-deletion snapshots.
  */
  deleteTrigger: TenantDeleteTriggerSchema.optional(),
  deactivatedAt: z.string().datetime({
    offset: true
  }).optional(),
  deletionDueAt: z.string().datetime({
    offset: true
  }).optional(),
  /**
  * Per-store erasure evidence (R10), present ONLY on the final `deleted`
  * snapshot — the last hop that carries core's evidence summary to operations
  * for the auditor download. Every other snapshot omits it, leaving the wire
  * unchanged. Reuses the tenant-deleted event's item schema.
  */
  evidence: z.array(TenantErasureEvidenceSchema).optional(),
  /** Core's log-only failure reason; surfaced to ops, never to the customer. */
  error: z.string().max(500).optional(),
  occurredAt: z.string().datetime({
    offset: true
  })
});
function offboardingStatusEnvelope(tenantId, idempotencyKey) {
  return {
    tenant_id: tenantId,
    idempotency_key: idempotencyKey,
    event_type: TENANT_LIFECYCLE_TOPICS.status,
    schema_version: TENANT_LIFECYCLE_SCHEMA_VERSION
  };
}
__name(offboardingStatusEnvelope, "offboardingStatusEnvelope");
function offboardingStatusExpectation(tenantId) {
  return {
    eventType: TENANT_LIFECYCLE_TOPICS.status,
    schemaVersion: TENANT_LIFECYCLE_SCHEMA_VERSION,
    ...tenantId !== void 0 ? {
      tenantId
    } : {}
  };
}
__name(offboardingStatusExpectation, "offboardingStatusExpectation");
var OFFBOARDING_APPS_EVIDENCE_EVENT = "offboarding-apps-evidence";
var AppsEvidenceReportSchema = z.object({
  tenantId: z.string().uuid(),
  /** Which app purged — evidence store names are prefixed with it. */
  source: z.enum([
    "dashboard"
  ]),
  evidence: z.array(TenantErasureEvidenceSchema).min(1),
  occurredAt: z.string().datetime({
    offset: true
  })
});
function offboardingAppsEvidenceEnvelope(tenantId, idempotencyKey) {
  return {
    tenant_id: tenantId,
    idempotency_key: idempotencyKey,
    event_type: OFFBOARDING_APPS_EVIDENCE_EVENT,
    schema_version: TENANT_LIFECYCLE_SCHEMA_VERSION
  };
}
__name(offboardingAppsEvidenceEnvelope, "offboardingAppsEvidenceEnvelope");
function offboardingAppsEvidenceExpectation(tenantId) {
  return {
    eventType: OFFBOARDING_APPS_EVIDENCE_EVENT,
    schemaVersion: TENANT_LIFECYCLE_SCHEMA_VERSION,
    ...tenantId !== void 0 ? {
      tenantId
    } : {}
  };
}
__name(offboardingAppsEvidenceExpectation, "offboardingAppsEvidenceExpectation");
var LLM_BACKEND_VALUES = [
  "openai",
  "vertex"
];
var LlmBackendSchema = z.enum(LLM_BACKEND_VALUES);
var CONFIG_VERSION_PATTERN = /^v?[0-9]+([.][0-9]+)*$/;
var CONFIG_VERSION_MAX_LENGTH = 32;
var ConfigVersionSchema = z.string().min(1).max(CONFIG_VERSION_MAX_LENGTH).regex(CONFIG_VERSION_PATTERN, "must look like 4.1 or v3.2.2");
var INGESTION_OAUTH_CLIENT_ID_PATTERN = /^[0-9]+$/;
var LLM_MODEL_MAX_LENGTH = 200;
var LLM_BASE_URL_MAX_LENGTH = 2048;
var LLM_BASE_URL_INTERNAL_HOST_SUFFIXES = [
  ".svc",
  ".svc.cluster.local"
];
var LLM_BASE_URL_LOCAL_HOSTS = [
  "localhost",
  "127.0.0.1",
  "host.docker.internal"
];
function llmBaseUrlProblem(value) {
  if (value.length > LLM_BASE_URL_MAX_LENGTH) {
    return `exceeds ${LLM_BASE_URL_MAX_LENGTH} characters`;
  }
  let url;
  try {
    url = new URL(value);
  } catch {
    return "must be a valid URL";
  }
  if (url.username !== "" || url.password !== "") return "must not embed credentials";
  const scheme = url.protocol.toLowerCase();
  if (scheme === "https:") return null;
  if (scheme !== "http:") return "must be an http(s) URL";
  const host = url.hostname.toLowerCase();
  const internal = LLM_BASE_URL_LOCAL_HOSTS.includes(host) || LLM_BASE_URL_INTERNAL_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix));
  return internal ? null : "plain http is allowed only for in-cluster (.svc) or local hosts";
}
__name(llmBaseUrlProblem, "llmBaseUrlProblem");
var LlmBaseUrlSchema = z.string().superRefine((value, ctx) => {
  const problem = llmBaseUrlProblem(value);
  if (problem) ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: `llmBaseUrl ${problem}`
  });
});
var INGESTION_OAUTH_CLIENT_ID_MAX_LENGTH = 64;
var SYSTEM_PROMPT_MAX_LENGTH = 2e5;
var TEMPLATE_JSON_MAX_LENGTH = 2e5;
var CORE_CONFIG_REQUEST_BODY_LIMIT = "1mb";
var RECOGNIZER_SET_VERSION_MAX_LENGTH = 32;
var GLINER_MODEL_NAME_MAX_LENGTH = 200;
var ACTOR_MAX_LENGTH = 200;
var commandBase2 = z.object({
  tenantId: z.string().uuid(),
  requestId: z.string().uuid()
});
var TENANT_CONFIG_FIELDS = [
  "llmModel",
  "llmBaseUrl",
  "llmBackend",
  "ingestionOauthClientId",
  "ingestionEntraClientId",
  "sources"
];
var TenantConfigUpdateCommandSchema = commandBase2.extend({
  llmModel: z.string().min(1).max(LLM_MODEL_MAX_LENGTH).nullable().optional(),
  // See llmBaseUrlProblem: https unless in-cluster/local, never with credentials.
  llmBaseUrl: LlmBaseUrlSchema.nullable().optional(),
  llmBackend: LlmBackendSchema.nullable().optional(),
  ingestionOauthClientId: z.string().regex(INGESTION_OAUTH_CLIENT_ID_PATTERN, "digits only").max(INGESTION_OAUTH_CLIENT_ID_MAX_LENGTH).nullable().optional(),
  ingestionEntraClientId: z.string().uuid().nullable().optional(),
  // Replace the whole settings.sources node (object) or remove it (null); omitted = untouched.
  sources: TenantSourcesSchema.nullable().optional()
}).refine((body) => TENANT_CONFIG_FIELDS.some((field) => field in body), {
  message: "at least one config field is required"
});
var PromptTemplateJsonSchema = z.object({
  groups: z.record(z.unknown()).optional(),
  schema: z.record(z.unknown()).optional()
}).passthrough().refine((template) => template.groups !== void 0 || template.schema !== void 0, {
  message: "templateJson must carry a groups object or a schema object"
}).refine((template) => JSON.stringify(template).length <= TEMPLATE_JSON_MAX_LENGTH, {
  message: `templateJson exceeds ${TEMPLATE_JSON_MAX_LENGTH} serialized characters`
});
var TenantPromptPromoteCommandSchema = commandBase2.extend({
  version: ConfigVersionSchema,
  systemPrompt: z.string().min(1).max(SYSTEM_PROMPT_MAX_LENGTH).optional(),
  templateJson: PromptTemplateJsonSchema.optional(),
  recognizerSetVersion: z.string().min(1).max(RECOGNIZER_SET_VERSION_MAX_LENGTH).optional(),
  glinerModelName: z.string().min(1).max(GLINER_MODEL_NAME_MAX_LENGTH).optional()
}).refine((body) => body.systemPrompt === void 0 === (body.templateJson === void 0), {
  message: "systemPrompt and templateJson must be given together"
});
var TenantLabelVersionUpdateCommandSchema = commandBase2.extend({
  labelVersion: ConfigVersionSchema,
  force: z.boolean().default(false)
});
var TENANT_CONFIG_COMMAND_SCHEMAS = {
  "tenant-config-update": TenantConfigUpdateCommandSchema,
  "tenant-prompt-promote": TenantPromptPromoteCommandSchema,
  "tenant-label-version-update": TenantLabelVersionUpdateCommandSchema
};
var TenantCoreConfigSnapshotSchema = z.object({
  llmModel: z.string().nullable(),
  llmBaseUrl: z.string().nullable(),
  llmBackend: z.string().nullable(),
  ingestionOauthClientId: z.string().nullable(),
  ingestionEntraClientId: z.string().nullable(),
  // Null when the tenant has no tenant_config row yet (a prompt-only write never
  // creates one) — never invent a version core did not store (design §7 S4).
  labelVersion: z.string().nullable(),
  // tenant_settings.settings.sources (TDEV-1176). Default, not required: a core
  // that predates the field omits the key, and its applied ack must still land
  // (an unparseable ack = mirror stuck at `requested`). null = "no lists stored",
  // which is exactly what that core can hold.
  sources: TenantSourcesSchema.nullable().default(null)
});
var TenantPromptSnapshotSchema = z.object({
  version: z.string(),
  promptSeq: z.number().int().nonnegative(),
  provider: z.string(),
  recognizerSetVersion: z.string(),
  glinerModelName: z.string().nullable()
});
var TENANT_CONFIG_APPLIED_EVENT = "tenant-config-applied";
var TenantConfigAppliedEventSchema = z.object({
  tenantId: z.string().uuid(),
  requestId: z.string().uuid(),
  action: TenantConfigActionSchema,
  config: TenantCoreConfigSnapshotSchema,
  prompt: TenantPromptSnapshotSchema.nullable(),
  appliedAt: z.string().datetime({
    offset: true
  })
});
var TENANT_CONFIG_STATUS_EVENT = "tenant-config-status";
var TENANT_CONFIG_REQUEST_STATUSES = [
  "requested",
  "applied",
  "failed"
];
var TENANT_CONFIG_ERROR_MAX_LENGTH = 500;
var statusBase = z.object({
  tenantId: z.string().uuid(),
  requestId: z.string().uuid(),
  action: TenantConfigActionSchema,
  occurredAt: z.string().datetime({
    offset: true
  })
});
var TenantConfigAppliedStatusSchema = statusBase.extend({
  status: z.literal("applied"),
  // REQUIRED on applied: an "applied" with no state would be stored as success
  // while the mirror keeps stale columns (Greptile apps#238 P2).
  snapshot: z.object({
    config: TenantCoreConfigSnapshotSchema,
    prompt: TenantPromptSnapshotSchema.nullable()
  })
});
var TenantConfigFailedStatusSchema = statusBase.extend({
  status: z.literal("failed"),
  error: z.string().min(1).max(TENANT_CONFIG_ERROR_MAX_LENGTH)
});
var TenantConfigStatusMessageSchema = z.discriminatedUnion("status", [
  TenantConfigAppliedStatusSchema,
  TenantConfigFailedStatusSchema
]);
function tenantConfigStatusEnvelope(tenantId, idempotencyKey) {
  return {
    tenant_id: tenantId,
    idempotency_key: idempotencyKey,
    event_type: TENANT_CONFIG_STATUS_EVENT,
    schema_version: TENANT_LIFECYCLE_SCHEMA_VERSION
  };
}
__name(tenantConfigStatusEnvelope, "tenantConfigStatusEnvelope");
function tenantConfigStatusExpectation(tenantId) {
  return {
    eventType: TENANT_CONFIG_STATUS_EVENT,
    schemaVersion: TENANT_LIFECYCLE_SCHEMA_VERSION,
    ...tenantId !== void 0 ? {
      tenantId
    } : {}
  };
}
__name(tenantConfigStatusExpectation, "tenantConfigStatusExpectation");
var TENANT_SCOPE_MANIFEST_REQUEST_ACTION = "tenant-scope-manifest-request";
var TenantScopeManifestRequestSchema = commandBase2;
function tenantScopeManifestRequestEnvelope(tenantId, requestId) {
  return {
    tenant_id: tenantId,
    idempotency_key: `${TENANT_SCOPE_MANIFEST_REQUEST_ACTION}:${tenantId}:${requestId}`,
    event_type: TENANT_SCOPE_MANIFEST_REQUEST_ACTION,
    schema_version: TENANT_LIFECYCLE_SCHEMA_VERSION
  };
}
__name(tenantScopeManifestRequestEnvelope, "tenantScopeManifestRequestEnvelope");
var TenantScopeManifestSourceSchema = z.object({
  source: z.string(),
  mode: z.enum(SCOPE_MODES),
  allow: z.array(z.string()),
  deny: z.array(z.string()),
  inScopeContainers: z.number().int().nonnegative(),
  // MATERIALIZED exclusions only — containers core recorded an `excluded` row for
  // (crawled, then a rule dropped). In allow mode the true excluded set is the
  // COMPLEMENT of `inScopeContainers` (core does not materialize a row per
  // non-allowed container — thousands on a per-user source), so read this with
  // `mode`: in allow mode the UI shows "all others excluded by construction",
  // never a bare `0`.
  excludedContainers: z.number().int().nonnegative(),
  excludedWithRetainedData: z.number().int().nonnegative(),
  // True ⇔ discover has not enumerated this source since the rules changed, so
  // the counts are a pre-change state (an allow-list set before the first crawl
  // reads excluded: 0 until discovery runs — "pending", not "nothing excluded").
  enforcementPending: z.boolean()
});
var TenantScopeManifestSchema = z.object({
  sources: z.array(TenantScopeManifestSourceSchema),
  attestation: z.object({
    zeroNewIngestion: z.boolean(),
    enforcementPending: z.boolean()
  })
});
var TENANT_SCOPE_MANIFEST_EVENT = "tenant-scope-manifest";
var TenantScopeManifestEventSchema = z.object({
  tenantId: z.string().uuid(),
  requestId: z.string().uuid(),
  manifest: TenantScopeManifestSchema,
  builtAt: z.string().datetime({
    offset: true
  })
});
function tenantScopeManifestEnvelope(tenantId, idempotencyKey) {
  return {
    tenant_id: tenantId,
    idempotency_key: idempotencyKey,
    event_type: TENANT_SCOPE_MANIFEST_EVENT,
    schema_version: TENANT_LIFECYCLE_SCHEMA_VERSION
  };
}
__name(tenantScopeManifestEnvelope, "tenantScopeManifestEnvelope");
function tenantScopeManifestExpectation(tenantId) {
  return {
    eventType: TENANT_SCOPE_MANIFEST_EVENT,
    schemaVersion: TENANT_LIFECYCLE_SCHEMA_VERSION,
    ...tenantId !== void 0 ? {
      tenantId
    } : {}
  };
}
__name(tenantScopeManifestExpectation, "tenantScopeManifestExpectation");
var PERMISSIONS = {
  usersView: "users.view",
  usersManage: "users.manage",
  tenantSettingsManage: "tenant-settings.manage",
  auditLogView: "audit-log.view",
  connectorsView: "connectors.view",
  connectorsManage: "connectors.manage",
  notificationsManage: "notifications.manage",
  dataView: "data.view",
  summariesSensitive: "summaries.sensitive",
  findingsView: "findings.view",
  findingsTriage: "findings.triage",
  findingsRemediate: "findings.remediate",
  promptRun: "prompt.run",
  dataExport: "data.export",
  quotaView: "quota.view"
};
var PERMISSION_SLUGS = Object.values(PERMISSIONS);
var WEB_PERMISSIONS = [
  "data.view",
  "users.view",
  "users.manage"
];
var NO_ACCESS_REASONS = {
  "data.view": "Ask a Super Admin for the Admin or Analyst role.",
  "users.view": "Ask a Super Admin for the Super Admin or Admin role.",
  "users.manage": "Ask a Super Admin for the Super Admin role."
};
function noAccessReason(permission, can) {
  return permission === "data.view" && can("users.manage") ? "Invite a second Super Admin to grant you a data role." : NO_ACCESS_REASONS[permission];
}
__name(noAccessReason, "noAccessReason");
var SEEDED_ROLES = {
  superAdmin: "Super Admin",
  admin: "Admin",
  analyst: "Analyst"
};
var SEEDED_ROLE_NAMES = Object.values(SEEDED_ROLES);
function sortRoles(roles) {
  const rank = /* @__PURE__ */ __name((r) => SEEDED_ROLE_NAMES.indexOf(r.name), "rank");
  return [
    ...roles
  ].sort((a, b) => rank(a) - rank(b));
}
__name(sortRoles, "sortRoles");
function groupRoles(rows) {
  const byOwner = /* @__PURE__ */ new Map();
  for (const { ownerId, id, name } of rows) {
    byOwner.set(ownerId, [
      ...byOwner.get(ownerId) ?? [],
      {
        id,
        name
      }
    ]);
  }
  for (const [ownerId, roles] of byOwner) byOwner.set(ownerId, sortRoles(roles));
  return byOwner;
}
__name(groupRoles, "groupRoles");
function activeSuperAdmins(admins, rolesByAdmin) {
  return admins.filter((a) => a.suspendedAt === null && (rolesByAdmin.get(a.id) ?? []).some((r) => r.name === SEEDED_ROLES.superAdmin));
}
__name(activeSuperAdmins, "activeSuperAdmins");
var RBAC_ERROR_CODES = {
  permissionDenied: "PERMISSION_DENIED",
  roleRequired: "ROLE_REQUIRED",
  roleUnknown: "ROLE_UNKNOWN",
  selfRoleChange: "SELF_ROLE_CHANGE",
  selfSuspend: "SELF_SUSPEND",
  lastSuperAdmin: "LAST_SUPER_ADMIN",
  invitationAccepted: "INVITATION_ACCEPTED",
  adminExists: "ADMIN_EXISTS"
};
var RoleRefSchema = z.object({
  id: z.string().uuid(),
  name: z.string()
});

// src/summary-access.ts
var TIERED_SUMMARY_SOURCES = {
  public: "document",
  internal: "document",
  confidential: "document",
  restricted: "document-type",
  "highly-confidential": "document-type"
};
function tieredSummaryLevels(source) {
  return SENSITIVITY_TIERS.filter((tier) => TIERED_SUMMARY_SOURCES[tier.id] === source).map((tier) => tier.level);
}
__name(tieredSummaryLevels, "tieredSummaryLevels");
function summaryAccessOf(held) {
  return held.includes(PERMISSIONS.summariesSensitive) ? "full" : "tiered";
}
__name(summaryAccessOf, "summaryAccessOf");
var DevAffordancesEnabledSchema = z.enum([
  "true",
  "false"
]).default("false").transform((v) => v === "true");
var SANDBOX_TIER = /^sandbox-.+$/;
function isDevOrSandboxEnvironment(environment) {
  return environment === "dev" || environment === "development" || SANDBOX_TIER.test(environment);
}
__name(isDevOrSandboxEnvironment, "isDevOrSandboxEnvironment");
function showsDevAffordances(config) {
  if (typeof config !== "object" || config === null) return false;
  if (config.devAffordancesEnabled !== true) return false;
  const { environment } = config;
  if (typeof environment !== "string") return false;
  return isDevOrSandboxEnvironment(environment);
}
__name(showsDevAffordances, "showsDevAffordances");
var MCP_PERMISSION = "data.view";
var MCP_CONSENT_PATH = "/mcp/authorize";
var MCP_GRANT_STATUSES = [
  "active",
  "expired",
  "revoked"
];
var McpOAuthClientInfoSchema = z.object({
  clientName: z.string(),
  /** Host the browser returns to after consent (spec: the user must see it). */
  redirectHost: z.string(),
  /** The redirect stays on the user's own machine (a CLI or desktop client). */
  isLoopback: z.boolean(),
  logoUri: z.string().url().optional()
});
var McpOAuthDecisionRequestSchema = z.object({
  approved: z.boolean(),
  params: z.record(z.string().max(4096))
}).strict();
var McpOAuthDecisionResponseSchema = z.object({
  redirectTo: z.string().url()
});
var McpGrantListItemSchema = z.object({
  id: z.string().uuid(),
  clientId: z.string(),
  clientName: z.string(),
  adminEmail: z.string(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  status: z.enum(MCP_GRANT_STATUSES)
});
var McpGrantListResponseSchema = z.object({
  grants: z.array(McpGrantListItemSchema)
});
var McpGrantRevokeResponseSchema = z.object({
  id: z.string().uuid(),
  revokedAt: z.string()
});

export { ACCESS_LEGITIMACY_ANALYSED_ONLY_HEADER, ACCESS_LEGITIMACY_CHART_AS_OF_HEADER, ACCESS_LEGITIMACY_PAGE_SIZE, ACCESS_LEGITIMACY_SCOPE_CONTRACT, ACCESS_LEGITIMACY_SCOPE_CONTRACT_HEADER, ACCESS_LEGITIMACY_SORT_DIR_VALUES, ACCESS_REQUEST_EDITABLE_STATUSES, ACCESS_REQUEST_SOURCE_VALUES, ACCESS_REQUEST_STATUS_VALUES, ACCESS_SCOPE_STALE, ACTOR_MAX_LENGTH, ALL_SERVICES, AdminSettingsSchema, AppsEvidenceReportSchema, BACKFILL_DAYS_VALUES, BACKFILL_OPTIONS, BackfillDaysSchema, CHANGE_DIRECTION_VALUES, CONFIG_VERSION_MAX_LENGTH, CONFIG_VERSION_PATTERN, CONNECTED_SERVICE_VALUES, CORE_CONFIG_REQUEST_BODY_LIMIT, ChangeDirection, ConfigVersionSchema, ConnectedServiceSchema, ConnectedServicesSchema, CoreTenantCreateMessageSchema, DAYS_FILTER_DEFAULT, DAYS_FILTER_OPTIONS, DAYS_FILTER_VALUES, DEFAULT_BACKFILL_DAYS, DEFAULT_TENANT_SIGN_IN_PROVIDER, DRIVE_ENTRY_MAX_LENGTH, DRIVE_LIST_MAX_ENTRIES, DashboardFeatureFlagOverridesSchema, DashboardFeatureFlagsSchema, DashboardSettingsSchema, DevAffordancesEnabledSchema, EMPTY_DRIVES_SOURCES, ENTRA_SAML_FIREBASE_PROVIDER_ID, EU_COUNTRY_CODES, EXPIRABLE_ONBOARDING_STATUSES, EffectiveSettingsSchema, FIREBASE_AUTH_DOMAIN_PATTERN, FirebaseAuthDomainSchema, GLINER_MODEL_NAME_MAX_LENGTH, GlobalFilterStateSchema, INGESTION_OAUTH_CLIENT_ID_MAX_LENGTH, INGESTION_OAUTH_CLIENT_ID_PATTERN, InvitationCreateCommandSchema, InvitationRevokeCommandSchema, LLM_BACKEND_VALUES, LLM_BASE_URL_INTERNAL_HOST_SUFFIXES, LLM_BASE_URL_LOCAL_HOSTS, LLM_BASE_URL_MAX_LENGTH, LLM_MODEL_MAX_LENGTH, LlmBackendSchema, LlmBaseUrlSchema, MAX_ADMINS_PER_TENANT, MAX_BACKFILL_DAYS, MAX_RETENTION_DAYS, MCP_CONSENT_PATH, MCP_GRANT_STATUSES, MCP_PERMISSION, McpGrantListItemSchema, McpGrantListResponseSchema, McpGrantRevokeResponseSchema, McpOAuthClientInfoSchema, McpOAuthDecisionRequestSchema, McpOAuthDecisionResponseSchema, NON_REVOCABLE_ONBOARDING_STATUSES, OFFBOARDING_APPS_EVIDENCE_EVENT, OFFBOARDING_LIFECYCLE_STATES, ONBOARDING_CONNECT_SUBMIT_SOURCES, ONBOARDING_DATA_SOURCES, ONBOARDING_ENV_HR_SYSTEMS, ONBOARDING_ENV_IDPS, ONBOARDING_ENV_WORKSPACES, ONBOARDING_FINISH_CONFLICT_CODES, ONBOARDING_GOOGLE_SCOPES_BY_ROLE, ONBOARDING_GUIDE_SECTIONS, ONBOARDING_HIBOB_API_BASE_URLS, ONBOARDING_JOURNEY_STATUSES, ONBOARDING_M365_GRAPH_PERMISSIONS_BY_ROLE, ONBOARDING_M365_MGMT_PERMISSIONS_BY_ROLE, ONBOARDING_MESSAGING_SUBSCRIPTIONS, ONBOARDING_MESSAGING_TOPICS, ONBOARDING_PROBE_FAILURE_REASONS, ONBOARDING_PROVIDER_ROLES, ONBOARDING_SETUP_STATUSES, ONBOARDING_SUPPORTED_SSO_PROVIDERS, ONBOARDING_WIZARD_STEP_IDS, OffboardingStatusMessageSchema, OnboardingConfigResponseSchema, OnboardingConnectDataGoogleSchema, OnboardingConnectDataHibobSavedSchema, OnboardingConnectDataHibobSchema, OnboardingConnectDataM365Schema, OnboardingConnectDataPrefillSchema, OnboardingConnectDataSchema, OnboardingConnectDataSubmitResponseSchema, OnboardingConnectDataSubmitSchema, OnboardingConnectSubmitSourceSchema, OnboardingDataSourceSchema, OnboardingEntraSamlServiceProviderSchema, OnboardingEntraSignInIdsSchema, OnboardingEntraSignInValidateResponseSchema, OnboardingEntraSignInValidateSchema, OnboardingEnvHrSystemSchema, OnboardingEnvIdpSchema, OnboardingEnvWorkspaceSchema, OnboardingEnvironmentSchema, OnboardingFeatureConfigSchema, OnboardingFeatureFlagsSchema, OnboardingFinishConflictBodySchema, OnboardingFinishConflictCodeSchema, OnboardingHibobApiBaseUrlSchema, OnboardingJourneyStatusMessageSchema, OnboardingProbeFailureReasonSchema, OnboardingProgressDataSchema, OnboardingProgressResponseSchema, OnboardingProgressSchema, OnboardingProviderOutcomeSchema, OnboardingProviderRoleSchema, OnboardingRateLimitTiersSchema, OnboardingSessionResponseSchema, OnboardingSetupStatusResponseSchema, OnboardingSetupStatusSchema, OnboardingSsoProviderSchema, OnboardingWizardStepIdSchema, PERMISSIONS, PERMISSION_SLUGS, PROVISIONING_SUBSCRIPTIONS, PROVISIONING_TOPICS, PartialAdminSettingsSchema, PartialTenantSettingsSchema, PromptTemplateJsonSchema, rate_limit_tiers_defaults_default as RATE_LIMIT_TIER_DEFAULTS, RBAC_ERROR_CODES, RECOGNIZER_SET_VERSION_MAX_LENGTH, REGION_VALUES, RoleRefSchema, SAML_CERTIFICATES_MAX, SAML_CERTIFICATE_MAX_BYTES, SCOPE_MODES, SEEDED_ROLES, SEEDED_ROLE_NAMES, SENSITIVITY_EXTERNAL_COLOR, SENSITIVITY_LEVELS, SENSITIVITY_ORDINALS, SENSITIVITY_TIERS, SENSITIVITY_UNCLASSIFIED_COLOR, SENSITIVITY_UNCLASSIFIED_ID, SENSITIVITY_UNCLASSIFIED_LABEL, SENSITIVITY_UNCLASSIFIED_LEVEL, SERVICE_FILTER_DEFAULT, SERVICE_FILTER_VALUES, SERVICE_REGISTRY, SIZE_UNITS, SYSTEM_PROMPT_MAX_LENGTH, SamlSignInConfigSchema, ScopedTenantSourcesSchema, SidebarStateSchema, TEMPLATE_JSON_MAX_LENGTH, TENANT_CONFIG_ACTIONS, TENANT_CONFIG_APPLIED_EVENT, TENANT_CONFIG_COMMAND_SCHEMAS, TENANT_CONFIG_ERROR_MAX_LENGTH, TENANT_CONFIG_FIELDS, TENANT_CONFIG_REQUEST_STATUSES, TENANT_CONFIG_STATUS_EVENT, TENANT_LIFECYCLE_ACTIONS, TENANT_LIFECYCLE_SCHEMA_VERSION, TENANT_LIFECYCLE_SUBSCRIPTIONS, TENANT_LIFECYCLE_TOPICS, TENANT_SCOPE_MANIFEST_EVENT, TENANT_SCOPE_MANIFEST_REQUEST_ACTION, TENANT_SIGN_IN_PROVIDERS, TENANT_SOURCE_KEYS, TOP_DATA_TAB_IDS, TenantActivateCommandSchema, TenantActivatedEventSchema, TenantCommandActionSchema, TenantConfigActionSchema, TenantConfigAppliedEventSchema, TenantConfigAppliedStatusSchema, TenantConfigFailedStatusSchema, TenantConfigStatusMessageSchema, TenantConfigUpdateCommandSchema, TenantCoreConfigSnapshotSchema, TenantCreateMessageSchema, TenantDeactivateCommandSchema, TenantDeactivateTriggerSchema, TenantDeactivatedEventSchema, TenantDeleteCommandSchema, TenantDeleteStartedEventSchema, TenantDeleteTriggerSchema, TenantDeletedEventSchema, TenantErasureEvidenceSchema, TenantHeldEventSchema, TenantHoldCommandSchema, TenantLabelVersionUpdateCommandSchema, TenantLifecycleActionSchema, TenantLifecycleFailedEventSchema, TenantPromptPromoteCommandSchema, TenantPromptSnapshotSchema, TenantReadyMessageSchema, TenantReleaseCommandSchema, TenantReleasedEventSchema, TenantScopeManifestEventSchema, TenantScopeManifestRequestSchema, TenantScopeManifestSchema, TenantScopeManifestSourceSchema, TenantSettingsSchema, TenantSignInProviderSchema, TenantSourcesSchema, WEB_PERMISSIONS, WidgetFilterStateSchema, WidgetOverrideSchema, WidgetPositionOverrideSchema, activeSuperAdmins, bytesToSize, defaultRegionForCountry, demoFlipSources, effectiveService, entraSamlIdpEndpoints, entraSamlServiceProvider, groupRoles, isChartAsOf, isConnectedService, isDevOrSandboxEnvironment, isInvitationExpired, isServiceOffered, isTenantConfigAction, isTimeFrameParam, llmBaseUrlProblem, noAccessReason, offboardingAppsEvidenceEnvelope, offboardingAppsEvidenceExpectation, offboardingStatusEnvelope, offboardingStatusExpectation, onboardingMessagingEnvelope, onboardingMessagingExpectation, onboardingPermissionsForRoles, onboardingProviderRolesFor, provisioningEnvelope, provisioningExpectation, regionFromGcpLocation, resolveFeatureFlags, sameDriveLists, scopeIsConsoleEditable, scopeModeFor, sensitivityLevelFor, sensitivityLevelsFor, showsDevAffordances, sortRoles, sourceHasSingleMode, summaryAccessOf, tenantConfigStatusEnvelope, tenantConfigStatusExpectation, tenantLifecycleEnvelope, tenantScopeManifestEnvelope, tenantScopeManifestExpectation, tenantScopeManifestRequestEnvelope, tieredSummaryLevels, toChangeDirection };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map